import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import AuthLayout from './AuthLayout';

// Política de contraseña (decisión D6): mínimo 10 caracteres + 1 mayús + 1 minús + 1 número.
const RULES = [
  { key: 'len',   label: 'Al menos 10 caracteres',    test: (v) => v.length >= 10 },
  { key: 'upper', label: 'Una letra mayúscula',       test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'Una letra minúscula',       test: (v) => /[a-z]/.test(v) },
  { key: 'digit', label: 'Un número',                  test: (v) => /[0-9]/.test(v) },
];

export default function FirstLoginScreen() {
  const navigate = useNavigate();
  const { session, profile, refreshProfile, signOut } = useAuth();
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checks = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(pwd1) })), [pwd1]);
  const allValid = checks.every((c) => c.ok) && pwd1 === pwd2 && pwd1.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!allValid) return;

    if (pwd1.toLowerCase().includes((profile?.correo || '').toLowerCase().split('@')[0] || 'xxxxxx')) {
      setError('La contraseña no puede contener tu nombre de usuario.');
      return;
    }

    setLoading(true);
    const { error: updateErr } = await supabase.auth.updateUser({ password: pwd1 });
    if (updateErr) {
      setLoading(false);
      setError(updateErr.message);
      return;
    }
    const { error: profileErr } = await supabase
      .from('profiles')
      .update({ requiere_cambio_password: false })
      .eq('id', session.user.id);
    setLoading(false);
    if (profileErr) {
      setError('Contraseña actualizada, pero no se pudo marcar tu perfil. Vuelve a entrar.');
      return;
    }
    await refreshProfile();
    navigate('/admin', { replace: true });
  };

  return (
    <AuthLayout
      title="Define tu nueva contraseña"
      subtitle={`Bienvenido/a ${profile?.nombre_completo || ''}. Por seguridad, debes cambiar la contraseña temporal antes de continuar.`}
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="first-login-form">
        <label className="block text-sm font-semibold text-[#0B1B5E]">
          Nueva contraseña
          <input
            type="password"
            required
            autoComplete="new-password"
            value={pwd1}
            onChange={(e) => setPwd1(e.target.value)}
            className="mt-1 w-full px-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] focus:border-[#5346E6] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#5346E6]/15 text-sm"
            data-testid="input-new-password"
          />
        </label>
        <label className="block text-sm font-semibold text-[#0B1B5E]">
          Confirmar nueva contraseña
          <input
            type="password"
            required
            autoComplete="new-password"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            className="mt-1 w-full px-3 py-3 rounded-xl border border-[#DDE6F7] bg-[#FAFBFF] text-[#0B1B5E] focus:border-[#5346E6] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#5346E6]/15 text-sm"
            data-testid="input-confirm-password"
          />
        </label>

        <ul className="space-y-1.5 mt-2" data-testid="password-rules">
          {checks.map((c) => (
            <li key={c.key} className={`flex items-center gap-2 text-xs ${c.ok ? 'text-[#1E9F73]' : 'text-[#7B86A6]'}`}>
              {c.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {c.label}
            </li>
          ))}
          <li className={`flex items-center gap-2 text-xs ${pwd1 && pwd2 && pwd1 === pwd2 ? 'text-[#1E9F73]' : 'text-[#7B86A6]'}`}>
            {pwd1 && pwd2 && pwd1 === pwd2 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            Las contraseñas coinciden
          </li>
        </ul>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[#FFE9E9] border border-[#FFC4C4] text-[#9B1C1C] text-xs" role="alert" data-testid="first-login-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={!allValid || loading}
          className="w-full py-3 rounded-xl bg-[#0B1B5E] hover:bg-[#5346E6] disabled:bg-[#8089AE] disabled:cursor-not-allowed text-white font-bold text-sm transition-colors shadow-[0_10px_24px_-10px_rgba(11,27,94,0.55)]"
          data-testid="first-login-submit"
        >
          {loading ? 'Guardando…' : 'Guardar contraseña y continuar'}
        </button>

        <button
          type="button"
          onClick={() => signOut().then(() => navigate('/login'))}
          className="w-full text-xs text-[#7B86A6] hover:text-[#0B1B5E] underline mt-2"
          data-testid="first-login-logout"
        >
          Cerrar sesión
        </button>
      </form>
    </AuthLayout>
  );
}
